import { ArrowRight, Check } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { findWorkflowStep } from '@/config/workflows';
import { useNavItemVisibility } from '@/hooks/use-nav-visibility';
import { cn } from '@/lib/utils';

/**
 * Langkah-langkah alur Rapor / SKTB di atas halaman yang termasuk alurnya.
 *
 * Dipasang sekali di kerangka, bukan di tiap halaman: halaman tidak perlu tahu
 * dirinya langkah keberapa, dan menambah langkah cukup di `@/config/workflows`.
 * Karena kerangkanya tidak ikut dilepas saat berpindah halaman, garis hijaunya
 * memanjang/memendek dengan animasi ketika pengguna maju atau mundur langkah.
 *
 * Hijau berarti "sudah dilewati dalam urutan", bukan "datanya sudah lengkap" —
 * stepper ini penunjuk jalan, bukan pemeriksa kelengkapan.
 */
export function WorkflowStepper() {
  const { pathname } = useLocation();
  const isVisible = useNavItemVisibility();
  const currentRef = useRef<HTMLAnchorElement>(null);

  const match = findWorkflowStep(pathname);
  const steps = match ? match.workflow.steps.filter(isVisible) : [];
  const currentIndex = match ? steps.indexOf(match.step) : -1;

  // Di layar sempit stepper bisa digulir mendatar; langkah yang sedang dibuka
  // dibawa ke tengah supaya tidak tersembunyi di luar layar.
  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [currentIndex]);

  // Satu langkah saja bukan alur — misalnya guru yang hanya melihat Kehadiran.
  if (!match || currentIndex === -1 || steps.length < 2) {
    return null;
  }

  const next = steps[currentIndex + 1];

  return (
    <nav
      aria-label={match.workflow.title}
      className="mb-6 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-card)] p-4 print:hidden"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">
          {match.workflow.title}
          <span className="ml-2 font-normal normal-case tracking-normal">
            · Langkah {currentIndex + 1} dari {steps.length}
          </span>
        </p>

        {next && (
          <Link
            to={next.href}
            className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] underline-offset-4 hover:underline"
          >
            Lanjut: {next.label}
            <ArrowRight className="size-3.5" aria-hidden="true" />
          </Link>
        )}
      </div>

      <ol className="scrollbar-thin -mx-1 flex overflow-x-auto px-1 py-1">
        {steps.map((step, index) => {
          const isDone = index < currentIndex;
          const isCurrent = index === currentIndex;

          return (
            <li key={step.href} className="relative min-w-[7.5rem] flex-1">
              {/* Garis ke langkah berikutnya, dari tengah lingkaran ini ke
                  tengah lingkaran sesudahnya. Isinya yang hijau memanjang
                  saat langkah ini sudah dilewati. */}
              {index < steps.length - 1 && (
                <div
                  className="absolute left-[calc(50%+1.25rem)] right-[calc(-50%+1.25rem)] top-5 h-1 -translate-y-1/2 overflow-hidden rounded-full bg-[var(--color-border)]"
                  aria-hidden="true"
                >
                  <div
                    className={cn(
                      'h-full rounded-full bg-[var(--color-success)] transition-[width] duration-500 ease-out',
                      isDone ? 'w-full' : 'w-0',
                    )}
                  />
                </div>
              )}

              <Link
                ref={isCurrent ? currentRef : undefined}
                to={step.href}
                aria-current={isCurrent ? 'step' : undefined}
                className="group flex flex-col items-center gap-2 rounded-[var(--radius-md)] px-1 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
              >
                <span
                  className={cn(
                    'relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold tabular-nums transition-colors duration-300',
                    isDone &&
                      'border-[var(--color-success)] bg-[var(--color-success)] text-[var(--color-success-foreground)]',
                    isCurrent &&
                      'border-[var(--color-success)] bg-[var(--color-success-subtle)] text-[var(--color-success-on-subtle)] ring-4 ring-[var(--color-success-subtle)]',
                    !isDone &&
                      !isCurrent &&
                      'border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)] group-hover:border-[var(--color-success)] group-hover:text-[var(--color-foreground)]',
                  )}
                >
                  {isDone ? <Check className="size-4" strokeWidth={3} aria-hidden="true" /> : index + 1}
                  {isDone && <span className="sr-only">Langkah {index + 1}, sudah dilewati:</span>}
                </span>

                <span className="min-w-0">
                  <span
                    className={cn(
                      'block text-xs leading-tight sm:text-sm',
                      isCurrent
                        ? 'font-semibold text-[var(--color-foreground)]'
                        : 'font-medium text-[var(--color-muted-foreground)] group-hover:text-[var(--color-foreground)]',
                    )}
                  >
                    {step.label}
                  </span>
                  <span className="mt-0.5 hidden text-[11px] leading-tight text-[var(--color-muted-foreground)] md:block">
                    {step.hint}
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
